import { Subject, Observable, Subscription } from 'rxjs';

export class MapZoomRegionController {
  private currentRegionLevel: number = 1;
  private loadedRegions: Map<string, any> = new Map(); // regionId -> region data
  private displayedPolygons: Map<string, any> = new Map(); // regionId -> polygon shape
  private zoomSubscription?: Subscription;
  private regionLevelChangeSubject = new Subject<number>();
  private regionLoadSubject = new Subject<{ level: number; parentId?: string }>();

  // Zoom thresholds for each region level
  private zoomThresholds = {
    level1: { min: 0, max: 8 },    // Country level - zoom 0-8
    level2: { min: 8, max: 11 },   // Province/State level - zoom 8-11
    level3: { min: 11, max: 19 }   // District level - zoom 11+
  };

  // Observables for external subscriptions
  regionLevelChange$: Observable<number> = this.regionLevelChangeSubject.asObservable();
  regionLoad$: Observable<{ level: number; parentId?: string }> = this.regionLoadSubject.asObservable();

  constructor(
    private mapInstance: any,
    private layersController: any,
    private eventsController: any
  ) {}

  /**
   * Initialize the zoom-based region controller
   */
  initialize(): void {
    // Subscribe to zoom changes
    this.zoomSubscription = this.eventsController.mapZoomChange$.subscribe((zoom: number) => {
      this.handleZoomChange(zoom);
    });

    // Load initial regions (level 1)
    this.loadRegionLevel(1);
  }

  /**
   * Handle zoom level changes and determine which region level to display
   */
  private handleZoomChange(zoom: number): void {
    const newLevel = this.determineRegionLevel(zoom);

    if (newLevel !== this.currentRegionLevel) {
      this.currentRegionLevel = newLevel;
      this.regionLevelChangeSubject.next(newLevel);
      this.updateDisplayedRegions(zoom);
    } else {
      // Same level but might need to show/hide child regions
      this.updateDisplayedRegions(zoom);
    }
  }

  /**
   * Determine which region level should be displayed based on zoom
   */
  private determineRegionLevel(zoom: number): number {
    if (zoom <= this.zoomThresholds.level1.max) {
      return 1;
    } else if (zoom <= this.zoomThresholds.level2.max) {
      return 2;
    } else {
      return 3;
    }
  }

  /**
   * Update displayed regions based on current zoom and viewport
   */
  private updateDisplayedRegions(zoom: number): void {
    const bounds = this.mapInstance.getBounds();
    const center = this.mapInstance.getCenter();

    // Clear current polygons
    this.clearDisplayedPolygons();

    if (zoom <= this.zoomThresholds.level1.max) {
      // Show level 1 regions
      this.displayRegionsByLevel(1);
    } else if (zoom <= this.zoomThresholds.level2.max) {
      // Show level 2 regions (children of visible level 1 regions)
      this.displayRegionsByLevel(2, bounds, center);
    } else {
      // Show level 3 regions (children of visible level 2 regions)
      this.displayRegionsByLevel(3, bounds, center);
    }
  }

  /**
   * Display regions of a specific level
   */
  private displayRegionsByLevel(level: number, bounds?: any, center?: any): void {
    const regionsToDisplay = Array.from(this.loadedRegions.values()).filter(
      region => region.level === level
    );

    if (regionsToDisplay.length === 0) {
      // No regions loaded for this level, trigger loading
      this.loadRegionLevel(level, bounds, center);
      return;
    }

    // If we need to filter by parent region (for levels 2 and 3)
    if (level > 1 && bounds) {
      const visibleParentRegions = this.getVisibleParentRegions(level - 1, bounds);

      if (visibleParentRegions.length === 0) {
        // Load parent level first
        this.loadRegionLevel(level - 1);
        return;
      }

      // Filter children by visible parents
      const visibleParentIds = new Set(visibleParentRegions.map(r => r.id));
      regionsToDisplay.forEach(region => {
        if (region.parentId && visibleParentIds.has(region.parentId)) {
          this.displayRegionPolygon(region);
        }
      });

      // Load missing child regions
      visibleParentRegions.forEach(parentRegion => {
        const hasChildren = regionsToDisplay.some(r => r.parentId === parentRegion.id);
        if (!hasChildren) {
          this.regionLoadSubject.next({ level, parentId: parentRegion.id });
        }
      });
    } else {
      // Display all regions of this level
      regionsToDisplay.forEach(region => {
        this.displayRegionPolygon(region);
      });
    }
  }

  /**
   * Get parent regions that are visible in current viewport
   */
  private getVisibleParentRegions(level: number, bounds: any): any[] {
    const regions = Array.from(this.loadedRegions.values()).filter(
      region => region.level === level
    );

    return regions.filter(region => {
      if (!region.lat || !region.lng) return false;
      const point = (window as any).L.latLng(region.lat, region.lng);
      return bounds.contains(point);
    });
  }

  /**
   * Display a region polygon on the map
   */
  private displayRegionPolygon(region: any): void {
    if (this.displayedPolygons.has(region.id)) {
      return; // Already displayed
    }

    if (!region.polygon || !region.coordinates) {
      return; // No geometry data
    }

    const color = this.getRegionColor(region.level);

    this.layersController.addDynamicPolygon(
      region.coordinates,
      {
        id: region.id,
        name: region.name || region.label,
        strokeColor: color,
        fillColor: color,
        fillOpacity: 0.1,
        strokeWidth: 2,
        opacity: 0.8,
        interactive: true,
        data: { region: region },
        onClick: (polygon: any) => this.handleRegionClick(polygon, region),
        onHover: (polygon: any) => this.handleRegionHover(polygon, region),
        onHoverOut: (polygon: any) => this.handleRegionHoverOut(polygon, region)
      }
    );

    this.displayedPolygons.set(region.id, region);
  }

  /**
   * Get color based on region level
   */
  private getRegionColor(level: number): string {
    const colors = {
      1: '#FF5722', // Red for level 1
      2: '#2196F3', // Blue for level 2
      3: '#4CAF50'  // Green for level 3
    };
    return colors[level as keyof typeof colors] || '#9E9E9E';
  }

  /**
   * Handle region click
   */
  private handleRegionClick(polygon: any, region: any): void {
    // Zoom into the region
    if (region.lat && region.lng) {
      const newZoom = this.getZoomForNextLevel(region.level);
      this.mapInstance.setView([region.lat, region.lng], newZoom, {
        animate: true,
        duration: 0.5
      });
    }
  }

  /**
   * Handle region hover
   */
  private handleRegionHover(polygon: any, region: any): void {
    // Highlight the region
    const shape = this.findShapeById(region.id);
    if (shape) {
      shape.setStyle({
        fillOpacity: 0.3,
        weight: 3
      });
    }
  }

  /**
   * Handle region hover out
   */
  private handleRegionHoverOut(polygon: any, region: any): void {
    // Remove highlight
    const shape = this.findShapeById(region.id);
    if (shape) {
      shape.setStyle({
        fillOpacity: 0.1,
        weight: 2
      });
    }
  }

  /**
   * Find a shape by region ID
   */
  private findShapeById(regionId: string): any {
    const allPolygons = this.layersController.getAllPolygons();
    const found = allPolygons.find((p: any) => p.properties?.id === regionId);
    return found?.polygon;
  }

  /**
   * Get appropriate zoom level for next region level
   */
  private getZoomForNextLevel(currentLevel: number): number {
    if (currentLevel === 1) return 10; // Zoom to show level 2
    if (currentLevel === 2) return 13; // Zoom to show level 3
    return 15; // Max zoom for level 3
  }

  /**
   * Clear all displayed polygons
   */
  private clearDisplayedPolygons(): void {
    this.layersController.clearDynamicPolygons();
    this.displayedPolygons.clear();
  }

  /**
   * Load regions for a specific level
   */
  private loadRegionLevel(level: number, bounds?: any, center?: any): void {
    this.regionLoadSubject.next({ level, parentId: undefined });
  }


  /**
   * Clear all loaded regions
   */
  clearRegions(): void {
    this.loadedRegions.clear();
    this.clearDisplayedPolygons();
  }

  /**
   * Destroy and cleanup
   */
  destroy(): void {
    if (this.zoomSubscription) {
      this.zoomSubscription.unsubscribe();
    }
    this.clearRegions();
  }
}

